import React, { useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SelectedAccount } from '@/features/selectedAccount';
import { StatementCacheProvider } from '@/features/selectedAccount/context/StatementCacheContext';
import { errorBoundary } from '@/hooks/withErrorBoundary';
import useHomeContext from '@/hooks/useHomeContext';
import LoadingScreen from '@/pages/common/LoadingScreen';
import { routes } from '@/utils/routes';
import { TWalletData } from '@/features/home/types/WalletData';

const EmergeAccounts: React.FC = () => {
  const { walletId } = useParams<{ walletId: string }>();
  const navigate = useNavigate();
  const {
    wallets,
    allAccounts,
    defaultOption,
    bankAccount,
    setBankAccount,
    setSelectedAccount,
    setAccountsFilter
  } = useHomeContext()!;

  // Sync wallet state from URL on mount and when wallets load (handles hard refresh)
  useEffect(() => {
    if (!walletId || wallets.length === 0) return;

    const wallet = wallets.find((w) => w.id === walletId);
    if (wallet) {
      setBankAccount(wallet);
      setSelectedAccount({
        label: wallet.name,
        labelDescription: Number(wallet.balance ?? 0).toFixed(2),
        value: wallet.name
      });
      setAccountsFilter([wallet.id]);
    } else {
      navigate(routes.home);
    }
  }, [walletId, wallets]);

  const handleOnSelectBankAccount = useCallback(
    (selected?: TWalletData) => {
      if (selected) {
        setBankAccount(selected);
        setSelectedAccount({
          label: selected.name,
          labelDescription: Number(selected.balance ?? 0).toFixed(2),
          value: selected.name
        });
        navigate(routes.selectedAccount.replace(':walletId', selected.id));
        return;
      }

      setBankAccount(allAccounts);
      setSelectedAccount(defaultOption);
      setAccountsFilter([]);
      navigate(routes.home);
    },
    [wallets, allAccounts, defaultOption]
  );

  // Hold render until bankAccount is synced to the URL param to prevent flash
  if (!bankAccount || bankAccount.id !== walletId) {
    return <LoadingScreen />;
  }

  return (
    <StatementCacheProvider>
      <SelectedAccount onSelectBankAccount={handleOnSelectBankAccount} showBreadcrumbs={false} />
    </StatementCacheProvider>
  );
};

export default errorBoundary(EmergeAccounts);
